import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { 
  Sparkles, 
  Edit3, 
  Target, 
  FileText,
  User,
  Building,
  Mail
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
interface ConversationTemplateResultsProps {
  conversationTemplate: any;
  toolId: string;
  currentLang: string;
  config: any;
  generatedQuestions: any[];
}

const ConversationTemplateResults: React.FC<ConversationTemplateResultsProps> = ({
  conversationTemplate,
  toolId,
  currentLang,
  config,
  generatedQuestions
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const { t } = useTranslation();
  const handleContactSubmit = async () => {
    if (!contactForm.name || !contactForm.email || !contactForm.company) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('send-contact-request', {
        body: {
          name: contactForm.name,
          email: contactForm.email,
          company: contactForm.company,
          message: contactForm.message,
          surveyConfig: config,
          questionsCount: generatedQuestions.length,
        },
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Request Sent Successfully!",
        description: "Your request has been sent to the Jigso team. We'll be in touch within 24-48 hours!",
      });
      
      setShowContactDialog(false);
      setContactForm({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Send Failed",
        description: "Failed to send your request. Please try again or contact us directly.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          <Card className="shadow-elegant bg-white/95 backdrop-blur-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl mb-2">{conversationTemplate.name}</CardTitle>
                  <p className="text-muted-foreground">{conversationTemplate.description}</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => navigate(`/${currentLang}/tools/${toolId}/configuration`)}
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Modify Configuration
                  </Button>
                  <Button onClick={() => setShowContactDialog(true)} className="bg-gradient-primary text-white">
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('results.tryInPlatform')}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                {conversationTemplate.stages?.map((stage: any, index: number) => (
                  <Card key={stage.id} className="border-l-4 border-l-primary/30">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 text-primary rounded-full flex items-center justify-center font-semibold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{stage.name}</CardTitle>
                          <p className="text-sm text-muted-foreground">{stage.description}</p>
                        </div>
                        <div className="ml-auto flex gap-2">
                          <Badge variant="outline">{stage.stage_type}</Badge>
                          {stage.timing_weeks !== null && (
                            <Badge variant="secondary">Week {stage.timing_weeks}</Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Questions */}
                      {stage.questions && stage.questions.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-primary" />
                            Questions ({stage.questions.length})
                          </h4>
                          <div className="space-y-3">
                            {stage.questions.map((question: any, qIndex: number) => (
                              <div key={question.id} className="bg-muted/30 p-4 rounded-lg">
                                <div className="flex items-start gap-3">
                                  <div className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-1">
                                    {qIndex + 1}
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium mb-2">{question.question_text}</p>
                                    <div className="flex gap-2">
                                      <Badge variant="outline" className="text-xs">
                                        {question.respondent_type}
                                      </Badge>
                                      {question.is_required && (
                                        <Badge variant="secondary" className="text-xs">Required</Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Objectives */}
                      {stage.objectives && stage.objectives.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <Target className="w-4 h-4 text-primary" />
                            Objectives ({stage.objectives.length})
                          </h4>
                          <div className="space-y-2">
                            {stage.objectives.map((objective: any, oIndex: number) => (
                              <div key={objective.id} className="bg-muted/30 p-3 rounded-lg">
                                <div className="flex items-start gap-3">
                                  <div className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-1">
                                    {oIndex + 1}
                                  </div>
                                  <p className="text-sm">{objective.objective_text}</p>
                                  {objective.is_placeholder && (
                                    <Badge variant="outline" className="text-xs ml-auto">
                                      Template
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Contact Dialog for conversation templates */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Try Template in Jigso Platform
            </DialogTitle>
            <DialogDescription>
              Interested in using this conversation template in the full Jigso platform? Fill out the form below and our team will get in touch with you.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="contact-name" className="flex items-center gap-1">
                <User className="w-4 h-4" />
                Full Name *
              </Label>
              <Input
                id="contact-name"
                value={contactForm.name}
                onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter your full name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contact-email" className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                Email Address *
              </Label>
              <Input
                id="contact-email"
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter your email address"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contact-company" className="flex items-center gap-1">
                <Building className="w-4 h-4" />
                Company *
              </Label>
              <Input
                id="contact-company"
                value={contactForm.company}
                onChange={(e) => setContactForm(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Enter your company name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contact-message">Additional Message</Label>
              <Textarea
                id="contact-message"
                value={contactForm.message}
                onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Tell us about your conversation template needs (optional)"
                rows={3}
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowContactDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleContactSubmit}
                className="flex-1 bg-gradient-primary text-white"
              >
                Send Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConversationTemplateResults; 